import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ error: any }>;
  signup: (email: string, password: string, displayName: string) => Promise<{ error: any }>;
  logout: () => Promise<void>;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const INACTIVITY_TIMEOUT = 1800000; // 30 minutes in milliseconds

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Initialize activity timestamp when user logs in
        if (session?.user) {
          localStorage.setItem('last_activity_time', Date.now().toString());
        }
      }
    );

    // Check for existing session (Supabase handles token validity)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // If there's a valid session, check inactivity
      if (session?.user) {
        const lastActivityTime = localStorage.getItem('last_activity_time');
        const now = Date.now();
        
        // Only logout if there WAS activity tracked and it's been too long
        if (lastActivityTime && (now - parseInt(lastActivityTime)) > INACTIVITY_TIMEOUT) {
          localStorage.setItem('auto_logout_reason', 'inactivity');
          supabase.auth.signOut();
          localStorage.removeItem('last_activity_time');
        } else if (!lastActivityTime) {
          // First time or missing timestamp - set it now
          localStorage.setItem('last_activity_time', Date.now().toString());
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Periodic inactivity check
  useEffect(() => {
    if (!user) return;

    const checkInactivity = setInterval(() => {
      const lastActivityTime = localStorage.getItem('last_activity_time');
      const now = Date.now();
      
      if (lastActivityTime && (now - parseInt(lastActivityTime)) > INACTIVITY_TIMEOUT) {
        // Save current path before logout so user can return to it
        const currentPath = window.location.pathname;
        if (currentPath !== '/auth') {
          localStorage.setItem('auth_redirect_path', currentPath);
        }
        localStorage.setItem('auto_logout_reason', 'inactivity');
        supabase.auth.signOut();
        localStorage.removeItem('last_activity_time');
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkInactivity);
  }, [user]);

  // Track user activity to update last activity timestamp
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      localStorage.setItem('last_activity_time', Date.now().toString());
    };

    // Track various user interaction events
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user]);

  const signup = async (email: string, password: string, displayName: string) => {
    // Check if profile with this email already exists
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('email')
      .eq('email', email)
      .maybeSingle();

    if (existingProfile) {
      return { 
        error: { 
          message: 'An account with this email already exists. Please log in instead.' 
        } 
      };
    }

    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName
        }
      }
    });
    
    return { error };
  };

  const login = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (!error && data.user) {
      localStorage.setItem('last_activity_time', Date.now().toString());
      
      // Check if profile exists, create if missing
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', data.user.id)
        .maybeSingle();
      
      if (!existingProfile) {
        await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            email: data.user.email || email,
            display_name: data.user.user_metadata?.display_name || 'New User'
          });
      }

      // Notify Trey Fleming when YC logs in
      if (data.user.email === 'ispai@ycombinator.com') {
        supabase.functions.invoke('notify-yc-login', {
          body: {
            userId: data.user.id,
            loginTimestamp: new Date().toISOString()
          }
        }).catch(err => console.error('Failed to send YC login notification:', err));
      }
    }
    
    return { error };
  };

  const logout = async () => {
    localStorage.removeItem('last_activity_time');
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isAuthenticated: !!user, 
      login, 
      signup, 
      logout,
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
